// @ts-nocheck
/* eslint-disable */
import React, { useState, useEffect } from 'react';
import EditEntity from '../../components/CRUD/EditEntity';
import { useTypedSelector } from '../../../helpers';
import axios from 'axios';
import { Modal } from 'antd';
import { Checkbox, FormControlLabel } from '@mui/material';
import DocumentTypeTransferList from '../document-types/DocumentTypeTransferList';
import { ApplicableFaresPanel, passesFareGuard } from '../shared/applicableFares';
import { useIntl } from 'react-intl';

import { useLocation, useHistory, useParams } from 'react-router-dom';

const EditDiscountBand = () => {
  const { pathname } = useLocation();
  const history = useHistory();
  const { id } = useParams();
  const isPromotionsRoute = pathname.includes('/fare-products/promotions');
  const productType = isPromotionsRoute ? 'Promotions' : 'FareProducts';
  const pageTitle = isPromotionsRoute ? 'EditPromotion' : 'EditFareProduct';
  const backUrl = isPromotionsRoute ? '/fare-products/promotions/:id' : '/fare-products/fare-products/:id';
  const productTerm = isPromotionsRoute ? 'Promotion' : 'Fare Product';
  const messageTransformer = (msg: string) => {
    if (!msg) return msg;
    return msg.replace(/discount bands/gi, `${productTerm}s`).replace(/discount band/gi, productTerm);
  };

  const userAuthData = useTypedSelector(state => state.userAuthData);
  const [fareProductWarningResolver, setFareProductWarningResolver] = useState(null);
  const [convertToPromotionResolver, setConvertToPromotionResolver] = useState(null);
  const [originalEntityData, setOriginalEntityData] = useState(null);
  const [assignmentsCount, setAssignmentsCount] = useState(null);
  const intl = useIntl();

  useEffect(() => {
    if (!id) return;
    const localAuthData = localStorage.getItem('authData');
    const userToken = localAuthData ? JSON.parse(localAuthData)?.userToken : null;
    const headers = {
      'access-token': userToken?.['access-token'],
      uid: userToken?.uid,
      client: userToken?.client,
    };
    axios.get(
      `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/web_portal/fare_media_policy_capping/fare_media_policy_capping_statistics/discount-bands/${id}/discount-band-assignments-count`,
      { headers }
    )
      .then(res => setAssignmentsCount(res.data?.data?.discount_band_assignments_count ?? null))
      .catch(() => setAssignmentsCount(null));
  }, [id]);

  const beforeSubmit = async (fv) => {
    // Zone-aware fare guard — see addDiscountBand for why the band level blocks
    // on the organisation-wide ceiling only and warns on the floor.
    const orgId = fv.organisations_id || originalEntityData?.organisations_id;
    if (fv.discount_mode === 'amount' && orgId && fv.valid_date_from) {
      const guardOk = await passesFareGuard({
        scope: 'band',
        discountAmount: fv.discount_amount,
        intl,
        body: {
          organisations_id: orgId,
          valid_date_from: fv.valid_date_from,
          valid_time_from: fv.valid_time_from || null,
        },
      });
      if (!guardOk) return { proceed: false };
    }

    if (isPromotionsRoute) {
      const orig = originalEntityData;
      if (!orig || !orig.is_fare_product) return Promise.resolve({ proceed: true });
      return new Promise((resolve) => {
        setConvertToPromotionResolver(() => (confirmed) => {
          setConvertToPromotionResolver(null);
          if (confirmed) {
            resolve({
              proceed: true,
              updatedValues: {
                ...fv,
                purchase_price: null,
                contract_duration_days: null,
                validity_days_after_activation: null,
                max_amount: null,
                max_trips: null,
                activation_trigger: null,
                requires_documents: false,
                document_types_ids: [],
                is_fare_product: false,
              },
            });
          } else {
            resolve({ proceed: false });
          }
        });
      });
    }
    const isEmpty = (v) => v === '' || v === null || v === undefined;
    const allFareFieldsEmpty =
      isEmpty(fv.purchase_price) &&
      isEmpty(fv.contract_duration_days) &&
      isEmpty(fv.max_amount) &&
      isEmpty(fv.max_trips);
    if (!allFareFieldsEmpty) return Promise.resolve({ proceed: true });
    return new Promise((resolve) => {
      setFareProductWarningResolver(() => (confirmed) => {
        setFareProductWarningResolver(null);
        if (confirmed) {
          resolve({
            proceed: true,
            updatedValues: { ...fv, requires_documents: false, document_types_ids: [], is_fare_product: false },
            backUrl: '/fare-products/promotions/:id',
          });
        } else {
          resolve({ proceed: false });
        }
      });
    });
  };

  const inputs = React.useMemo(() => [
    { name: 'code', labelKey: 'Code', type: 'text', required: true, disabled: assignmentsCount !== null && assignmentsCount > 0 },
    { name: 'name', labelKey: 'ArName', type: 'text', required: true },
    { name: 'en_name', labelKey: 'EnName', type: 'text', required: true },
    {
      name: 'organisations_name',
      labelKey: 'Organisation',
      type: 'text',
      disabled: true
    },
    {
      name: 'organisations_id',
      inputType: 'hidden'
    },
    {
      name: 'discount_mode',
      labelKey: 'DiscountCalculation',
      type: 'radioGroup',
      disabled: assignmentsCount !== null && assignmentsCount > 0,
      options: [
        { value: 'pct', labelKey: 'Percentage' },
        { value: 'amount', labelKey: 'Amount' }
      ]
    },
    { name: 'discount_pct', labelKey: 'Percentage', type: 'number', required: true, visibleIf: (values) => values.discount_mode === 'pct', disabled: assignmentsCount !== null && assignmentsCount > 0 },
    { name: 'discount_amount', labelKey: 'Amount', type: 'number', required: true, visibleIf: (values) => values.discount_mode === 'amount', disabled: assignmentsCount !== null && assignmentsCount > 0 },

    { type: 'divider', labelKey: 'FareProductOptions', hidden: isPromotionsRoute },
    {
      name: 'purchase_price',
      labelKey: 'PurchasePrice',
      type: 'number',
      required: false,
      hidden: isPromotionsRoute,
      disabled: assignmentsCount !== null && assignmentsCount > 0,
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if (!hasPP && (hasDur || hasMax || hasMaxTrips)) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },
    {
      name: 'contract_duration_days',
      labelKey: 'ContractDurationDays',
      type: 'number',
      required: false,
      hidden: isPromotionsRoute,
      disabled: assignmentsCount !== null && assignmentsCount > 0,
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if (!hasDur && (hasPP || hasMax || hasMaxTrips)) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },
    {
      name: 'max_amount',
      labelKey: 'MaxAmount',
      type: 'number',
      required: false,
      hidden: isPromotionsRoute,
      disabled: assignmentsCount !== null && assignmentsCount > 0,
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if (!hasMax && !hasMaxTrips && (hasPP || hasDur)) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },
    {
      name: 'max_trips',
      labelKey: 'MaxTrips',
      type: 'number',
      required: false,
      hidden: isPromotionsRoute,
      disabled: assignmentsCount !== null && assignmentsCount > 0,
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if (!hasMax && !hasMaxTrips && (hasPP || hasDur)) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },
    {
      name: 'activation_trigger',
      labelKey: 'ActivationTrigger',
      type: 'radioGroup',
      defaultValue: null,
      hidden: isPromotionsRoute,
      disabled: assignmentsCount !== null && assignmentsCount > 0,
      options: [
        { value: 'FIRST_USE', labelKey: 'FirstUse' },
        { value: 'SPECIFIC_DATE', labelKey: 'SpecificDate' }
      ],
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if ((hasPP || hasDur || hasMax || hasMaxTrips) && !values.activation_trigger) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },

    { type: 'divider', labelKey: 'Validity' },
    { name: 'valid_date_from', labelKey: 'ValidFrom', type: 'date', required: true, style: { width: '270px' } },
    {
      name: 'valid_date_to',
      labelKey: 'ValidTo',
      type: 'date',
      disabled: (values) => !!values.is_no_expiry,
      style: { width: '270px' },
      renderExtra: (value, options, intl, setFormValues, formValues) => (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={!!formValues.is_no_expiry}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormValues(prev => ({
                  ...prev,
                  is_no_expiry: checked,
                  valid_date_to: checked ? '' : (prev.valid_date_to || '')
                }));
              }}
              sx={{ color: '#FF6B38', '&.Mui-checked': { color: '#FF6B38' } }}
            />
          }
          label={intl?.formatMessage({ id: 'NoExpiry' }) || "No Expiry"}
          sx={{ whiteSpace: 'nowrap' }}
        />
      )
    },
    {
      name: 'valid_time_from',
      labelKey: 'TimeFrom',
      type: 'time',
      disabled: (values) => !!values.is_all_day,
      style: { width: '270px' },
      renderExtra: (value, options, intl, setFormValues, formValues) => (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={!!formValues.is_all_day}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormValues(prev => ({
                  ...prev,
                  is_all_day: checked,
                  valid_time_from: checked ? '00:00' : (prev.valid_time_from || ''),
                  valid_time_to: checked ? '23:59' : (prev.valid_time_to || '')
                }));
              }}
              sx={{ color: '#FF6B38', '&.Mui-checked': { color: '#FF6B38' } }}
            />
          }
          label={intl?.formatMessage({ id: 'AllDay' }) || "All Day"}
          sx={{ whiteSpace: 'nowrap' }}
        />
      )
    },
    {
      name: 'valid_time_to',
      labelKey: 'TimeTo',
      type: 'time',
      disabled: (values) => !!values.is_all_day,
      style: { width: '270px' }
    },
    // Hidden state fields — populated by onDataFetched, drive checkbox UI
    {
      name: 'is_all_day',
      type: 'hidden',
    },
    {
      name: 'is_no_expiry',
      type: 'hidden',
    },
    {
      name: 'days_of_week_group',
      type: 'checkboxGroup',
      renderExtra: (value, options, intl, setFormValues, formValues) => {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const isAllSelected = days.every(day => !!formValues[day]);
        return (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={isAllSelected}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormValues(prev => {
                    const next = { ...prev };
                    days.forEach(day => next[day] = checked);
                    return next;
                  });
                }}
                sx={{ color: '#FF6B38', '&.Mui-checked': { color: '#FF6B38' } }}
              />
            }
            label={intl?.formatMessage({ id: 'AllDays' }) || "All Days"}
            sx={{ my: 0 }}
          />
        );
      },
      options: [
        { name: 'sunday', labelKey: 'sunday' },
        { name: 'monday', labelKey: 'monday' },
        { name: 'tuesday', labelKey: 'tuesday' },
        { name: 'wednesday', labelKey: 'wednesday' },
        { name: 'thursday', labelKey: 'thursday' },
        { name: 'friday', labelKey: 'friday' },
        { name: 'saturday', labelKey: 'saturday' }
      ]
    },
    { name: 'requires_documents', labelKey: 'RequiresDocuments', type: 'checkbox', defaultValue: false, hidden: isPromotionsRoute },
    {
      name: 'document_types_ids',
      type: 'custom',
      defaultValue: [],
      hidden: isPromotionsRoute,
      visibleIf: (values) => !!values.requires_documents,
      render: (formValues, setFormValues, intl, dropdownOptions) => (
        <div style={{width: '100%', padding: '16px 0', gridColumn: '1 / -1'}}>
          <DocumentTypeTransferList
              selectedIds={formValues.document_types_ids || []}
              onChange={(ids) => setFormValues(prev => ({ ...prev, document_types_ids: ids }))}
          />
        </div>
      )
    }
  ].filter(input => !input.hidden), [isPromotionsRoute, intl, assignmentsCount]);

  return (
    <>
    <EditEntity
      pageTitle={pageTitle}
      subsystemName={productType}
      permissionSystem="fares"
      backUrl={backUrl}
      fetchApiUrl={`${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/web_portal/fare_media_policy_capping/discount_band/:id`}
      fetchApiMethod="GET"
      dataPath="data.data"
      updateApiUrl={`${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/web_portal/fare_media_policy_capping/discount_band/:id`}
      updateApiMethod="PATCH"
      itemsPerColumn={10}
      inputs={inputs}
      
      dropdowns={[]}
      
      onDataFetched={(data) => {
        setOriginalEntityData({ ...data });
        data.discount_mode = data.discount_pct ? 'pct' : 'amount';
        if (data.valid_date_from) data.valid_date_from = data.valid_date_from.split('T')[0];
        if (data.valid_date_to) data.valid_date_to = data.valid_date_to.split('T')[0];
        // Populate v6 fare product fields — prefer new columns, fall back to legacy
        data.contract_duration_days = data.contract_duration_days || data.validity_days_after_activation || null;
        data.max_amount = data.max_amount || null;
        data.max_trips  = data.max_trips  || null;
        if (data.activation_trigger === 'IMMEDIATE') {
          data.activation_trigger = 'FIRST_USE'; // Fallback for legacy data
        }
        
        if (data.required_document_types && Array.isArray(data.required_document_types)) {
          data.document_types_ids = data.required_document_types.map(rd => rd.id);
        } else {
          data.document_types_ids = [];
        }
        
        // Detect All Day — handles all possible time formats from backend
        const timeFrom = data.valid_time_from;
        const timeTo = data.valid_time_to;
        const isFromMidnight = !timeFrom || timeFrom === '00:00' || timeFrom === '00:00:00' || timeFrom.startsWith('00:00');
        const isToEndOfDay = !timeTo || timeTo === '23:59' || timeTo === '23:59:59' || timeTo === '23:59:00' || timeTo.startsWith('23:59');
        data.is_all_day = isFromMidnight && isToEndOfDay;
        
        // Ensure UI fields have values if All Day
        if (data.is_all_day) {
            data.valid_time_from = '00:00';
            data.valid_time_to = '23:59';
        }

        // Detect No Expiry
        data.is_no_expiry = !data.valid_date_to || data.valid_date_to === "" || data.valid_date_to === null;
        if (data.is_no_expiry) {
            data.valid_date_to = '';
        }

        // Populate the disabled org name text field from the resolved JOIN columns
        const lang = localStorage.getItem('EASY_MOBILITY_LANGUAGE');
        data.organisations_name = lang === 'ar-eg'
          ? (data.org_ar_name || data.org_en_name || '')
          : (data.org_en_name || data.org_ar_name || '');
      }}

      buildUpdatePayload={(values) => {
        const payload = { ...values };

        // Strictly clean both values first to prevent sending both.
        // This handles cases where one mode was saved previously but we are switching and sending PATCH
        payload.discount_pct = null;
        payload.discount_amount = null;

        if (values.discount_mode === 'pct') {
          const val = values.discount_pct;
          payload.discount_pct = (val !== null && val !== "" && val !== undefined) ? parseFloat(val) : null;
        } else if (values.discount_mode === 'amount') {
          const val = values.discount_amount;
          payload.discount_amount = (val !== null && val !== "" && val !== undefined) ? parseFloat(val) : null;
        }

        // Handle empty times/dates/dropdowns before sending to backend
        if (!payload.valid_time_from) payload.valid_time_from = null;
        if (!payload.valid_time_to) payload.valid_time_to = null;
        if (!payload.valid_date_to) payload.valid_date_to = null;

        delete payload.discount_mode;
        delete payload.days_of_week_group;
        delete payload.is_all_day;
        delete payload.is_no_expiry;

        if (payload.purchase_price === '') payload.purchase_price = null;
        if (payload.contract_duration_days === '') payload.contract_duration_days = null;
        if (payload.max_amount === '') payload.max_amount = null;
        if (payload.max_trips === '') payload.max_trips = null;
        // Keep legacy alias in sync for backward compat
        payload.validity_days_after_activation = payload.contract_duration_days || null;
        if (!payload.contract_duration_days) {
          payload.activation_trigger = null;
          payload.max_trips = null; // max_trips only applies to fare products
        }

        // DB constraint chk_fare_product_fields: if duration is set, trigger must also be set
        if (payload.contract_duration_days && !payload.activation_trigger) {
          throw new Error(intl.formatMessage({ id: 'ActivationTriggerRequired' }));
        }

        payload.is_fare_product = !!(
          payload.contract_duration_days || payload.activation_trigger ||
          payload.purchase_price || payload.max_amount || payload.max_trips
        );
        
        // Use the explicit checkbox value, not derived from array length.
        // When requires_documents is false, send empty array so backend deletes associations.
        const requiresDocs = !!payload.requires_documents;
        payload.requires_documents = requiresDocs;
        if (!requiresDocs) {
          payload.document_types_ids = [];
        }

        // Validate: if requires_documents is true, user must select at least one doc type
        if (requiresDocs && (!payload.document_types_ids || payload.document_types_ids.length === 0)) {
          throw new Error(intl.formatMessage({ id: 'RequiresDocumentsValidation' }));
        }

        return payload;
      }}
      successMessage={isPromotionsRoute ? 'PromotionUpdatedSuccessfully' : 'FareProductUpdatedSuccessfully'}
      messageTransformer={messageTransformer}
      beforeSubmit={beforeSubmit}
      renderAboveFields={(formValues, setFormValues, intl) => {
        const orig = originalEntityData;
        if (!orig) return null;
        const recordIsFareProduct = !!orig.is_fare_product;

        if (isPromotionsRoute) {
          // Record is a real promotion (no fare product values) and has no assignments → show convert
          if (!recordIsFareProduct && assignmentsCount === 0) {
            return (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 16 }}>
                <button
                  type="button"
                  className="updateBtnStyle"
                  style={{ backgroundColor: '#0FA66D', marginBottom: '10px' }}
                  onClick={() => history.push(`/fare-products/fare-products/${id}/edit?came_from=one`)}
                >
                  {intl.formatMessage({ id: 'ConvertToFareProduct' })}
                </button>
              </div>
            );
          }
          return null;
        }

        // Fare product route: only show "Convert to Promotion" if record has fare product values AND no assignments
        if (!recordIsFareProduct || assignmentsCount !== 0) return null;
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 16 }}>
            <button
              type="button"
              className="updateBtnStyle"
              style={{ backgroundColor: '#0FA66D' }}
              onClick={() => history.push(`/fare-products/promotions/${id}/edit?came_from=one`)}
            >
              {intl.formatMessage({ id: 'ConvertToPromotion' })}
            </button>
          </div>
        );
      }}
      renderAboveSubmit={(formValues, setFormValues, intl) => (
        <ApplicableFaresPanel
          scope="band"
          intl={intl}
          discountAmount={formValues.discount_mode === 'amount' ? formValues.discount_amount : null}
          missingKey="PleaseSelectOrgAndDate"
          hintKey="DiscountBandFaresHint"
          buildBody={() => {
            const orgId = formValues.organisations_id || originalEntityData?.organisations_id;
            if (!orgId || !formValues.valid_date_from) return null;
            return {
              organisations_id: orgId,
              valid_date_from: formValues.valid_date_from,
              valid_time_from: formValues.valid_time_from || null,
            };
          }}
        />
      )}
    />
    <Modal
      open={!!fareProductWarningResolver}
      title={intl.formatMessage({ id: 'FareProductEmptyFieldsWarningTitle' })}
      closable={false}
      footer={null}
    >
      <p style={{ marginBottom: 20 }}>
        {intl.formatMessage({ id: 'FareProductEmptyFieldsWarningMsg' })}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => fareProductWarningResolver(false)}
          className="updateBtnStyle"
          style={{ backgroundColor: '#6c757d' }}
        >
          {intl.formatMessage({ id: 'GoBackFillFields' })}
        </button>
        <button
          type="button"
          onClick={() => fareProductWarningResolver(true)}
          className="updateBtnStyle"
          style={{ backgroundColor: '#ff4d4f' }}
        >
          {intl.formatMessage({ id: 'ContinueAsPromotion' })}
        </button>
      </div>
    </Modal>
    <Modal
      open={!!convertToPromotionResolver}
      title={intl.formatMessage({ id: 'ConvertToPromotionWarningTitle' })}
      closable={false}
      footer={null}
    >
      <p style={{ marginBottom: 20 }}>
        {intl.formatMessage({ id: 'ConvertToPromotionWarningMsg' })}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => convertToPromotionResolver(false)}
          className="updateBtnStyle"
          style={{ backgroundColor: '#6c757d' }}
        >
          {intl.formatMessage({ id: 'GoBackKeepFareProduct' })}
        </button>
        <button
          type="button"
          onClick={() => convertToPromotionResolver(true)}
          className="updateBtnStyle"
          style={{ backgroundColor: '#ff4d4f' }}
        >
          {intl.formatMessage({ id: 'ContinueConvertToPromotion' })}
        </button>
      </div>
    </Modal>
    </>
  );
};

export default EditDiscountBand;
